import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextButton, useMediaQuery } from 'bonfire-kindling';

import { Loader, StackedList } from 'components';
import { useInfiniteScroll } from 'helpers/hooks';
import { useInfiniteSavedSearchProfiles } from 'helpers/opportunities';
import SavedSearchSettingsListItem from './SavedSearchSettingsListItem';

const ResponsiveList = ({ children }) => {
  const { t } = useTranslation();
  const isTabletOrDesktop = useMediaQuery(theme => theme.breakpoints.up('sm'));

  return isTabletOrDesktop ? (
    <Box
      sx={{
        '& > * + *': {
          mt: 4,
        },
        display: 'flex',
        flexDirection: 'column',
        listStyleType: 'none',
        px: 0,
        m: 0,
        mt: 4,
      }}
    >
      {children}
    </Box>
  ) : (
    <StackedList title={t('settings.saved_search.overview.mobile_secondary_title')}>
      {children}
    </StackedList>
  );
};

const SavedSearchSettings = () => {
  const { t } = useTranslation();
  const isTabletOrDesktop = useMediaQuery(theme => theme.breakpoints.up('sm'));
  const { searchProfiles, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteSavedSearchProfiles();

  const loadTriggerRef = useInfiniteScroll(
    useCallback(() => {
      fetchNextPage();
    }, [fetchNextPage]),
    { rootMargin: '200px' }
  );

  return (
    <>
      <Typography variant="h1" component="h2" sx={{ mb: 4 }}>
        {t('settings.saved_search.overview.title')}
      </Typography>
      <Box sx={{ width: '100%' }}>
        <TextButton
          label={t('settings.saved_search.overview.add_saved_search')}
          component={RouterLink}
          to="/settings/saved/new"
          variant={isTabletOrDesktop ? 'outline' : 'contained'}
          sx={{
            ml: isTabletOrDesktop ? 'auto' : 0,
            mb: isTabletOrDesktop ? 0 : 4,
            display: isTabletOrDesktop ? 'block' : 'inline-flex',
            width: isTabletOrDesktop ? 'fit-content' : '100%',
          }}
        />
      </Box>
      {isLoading ? (
        <Loader variant="puff" isCentered />
      ) : searchProfiles.length ? (
        <>
          <ResponsiveList>
            {searchProfiles.map(profile => (
              <SavedSearchSettingsListItem key={profile.id} {...profile} />
            ))}
          </ResponsiveList>
          <Box>
            {hasNextPage &&
              (isFetchingNextPage ? (
                <Loader variant="puff" isCentered />
              ) : (
                <div ref={loadTriggerRef} />
              ))}
          </Box>
        </>
      ) : null}
    </>
  );
};

export default SavedSearchSettings;
